import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, ExternalLink, Zap } from 'lucide-react';
import Navbar from './Navbar';
import Footer from './Footer';
import { products } from './Products';

import magicMoonImg from '../assets/serveses/Screenshot 2026-08-20 141258.png';
import sruthiDigitalsImg from '../assets/serveses/Screenshot 2026-08-20 141323.png';
import kanakaGuruImg from '../assets/serveses/Screenshot 2026-08-20 141452.png';
import samHealthImg from '../assets/serveses/Screenshot 2026-08-20 141717.png';

const clientProjects = [
  {
    name: 'Sruthi Digitals',
    logo: sruthiDigitalsImg,
    industry: 'Digital Marketing',
    accent: '#EAB308',
    bg: 'from-yellow-50 to-amber-50',
    border: 'border-yellow-200',
    brief:
      'A full-stack digital marketing platform empowering local businesses with smart campaign tools, analytics dashboards, and automated lead generation — built to scale from day one.',
    tags: ['Marketing', 'Analytics', 'Lead Gen'],
  },
  {
    name: 'Sam Health',
    logo: samHealthImg,
    industry: 'Healthcare',
    accent: '#1E3A8A',
    bg: 'from-blue-50 to-sky-50',
    border: 'border-blue-200',
    brief:
      'An integrated health management solution offering patient scheduling, digital records, telemedicine support, and real-time reporting — designed to modernize clinical workflows.',
    tags: ['Healthcare', 'Telemedicine', 'EHR'],
  },
  {
    name: 'Kanaka Guru Peeta',
    logo: kanakaGuruImg,
    industry: 'Education & Spirituality',
    accent: '#F97316',
    bg: 'from-orange-50 to-amber-50',
    border: 'border-orange-200',
    brief:
      'A digital platform preserving and broadcasting spiritual teachings, event management, and community engagement tools — bridging ancient wisdom with modern digital outreach.',
    tags: ['Education', 'Community', 'Events'],
  },
  {
    name: 'Scholars World',
    logo: null,
    industry: 'Edutech',
    accent: '#4F46E5',
    bg: 'from-indigo-50 to-violet-50',
    border: 'border-indigo-200',
    brief:
      'A comprehensive e-learning ecosystem with personalised learning paths, live tutoring sessions, competitive exam prep tools, and performance tracking for students K–12 and beyond.',
    tags: ['E-Learning', 'Exam Prep', 'Tutoring'],
    initial: 'S',
    color: '#4F46E5',
  },
  {
    name: 'Magic Moon Studios',
    logo: magicMoonImg,
    industry: 'Creative & Media',
    accent: '#0B4B5A',
    bg: 'from-teal-50 to-cyan-50',
    border: 'border-teal-200',
    brief:
      'A creative studio management suite handling project pipelines, client collaboration portals, asset version control, and invoice automation — purpose-built for creative agencies.',
    tags: ['Media', 'Studio Mgmt', 'Collaboration'],
  },
  {
    name: 'KGF Khanana',
    logo: null,
    industry: 'Food Delivery',
    accent: '#E11D48',
    bg: 'from-rose-50 to-pink-50',
    border: 'border-rose-200',
    brief:
      'A feature-rich food delivery platform connecting local restaurants with hungry customers — offering real-time order tracking, smart menu management, rider dispatch, and seamless payment integration.',
    tags: ['Food Delivery', 'Real-time', 'Payments'],
    initial: 'K',
    color: '#E11D48',
  },
];

export default function Projects() {
  const [activeCategory, setActiveCategory] = useState('All');
  
  const categories = ['All', 'Healthcare', 'Edutech', 'Sales', 'Hotels'];
  const filteredProducts = activeCategory === 'All' 
    ? products 
    : products.filter(p => p.category === activeCategory);

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 overflow-hidden relative">
      <div className="relative z-50">
        <Navbar forceOpaque={true} />
      </div>

      {/* Hero Section */}
      <div className="relative pt-32 pb-20 px-6 lg:px-16 overflow-hidden">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-brand-blue/5 rounded-full blur-[120px] pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-brand-orange/5 rounded-full blur-[150px] pointer-events-none" />

        <div className="max-w-7xl mx-auto w-full text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-brand-blue/30 bg-brand-blue/5 text-brand-blue text-sm font-semibold mb-8">
              <span className="w-2 h-2 rounded-full bg-brand-blue animate-pulse"></span>
              Our Work
            </div>
            
            <h1 className="text-5xl lg:text-7xl font-extrabold mb-6 font-syne leading-[1.15] text-slate-900">
              Transforming Ideas into <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-blue to-brand-orange">Digital Reality</span>
            </h1>

            <p className="text-slate-600 text-lg lg:text-xl mb-12 max-w-3xl mx-auto font-inter leading-relaxed">
              At QuBright, we build scalable, high-performance software that solves real-world problems. 
              Explore our portfolio of flagship products and bespoke client solutions.
            </p>
          </motion.div>
        </div>
      </div>

      {/* Products List Section */}
      <div className="py-20 bg-white relative z-10">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-16">
            <h2 className="font-syne font-extrabold text-3xl md:text-4xl text-slate-900 mb-4">
              Our Flagship Products
            </h2>
            <div className="w-20 h-1.5 bg-gradient-to-r from-brand-blue to-brand-orange rounded-full"></div>
          </div>

          <motion.div
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            className="flex mb-12 w-full"
          >
            <div className="inline-flex max-w-full overflow-x-auto p-1.5 rounded-2xl bg-slate-50 border border-slate-200 shadow-sm gap-1" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`relative px-6 py-2.5 rounded-xl font-space font-semibold text-sm transition-all duration-300 whitespace-nowrap
                    ${activeCategory === cat ? 'text-white shadow-md' : 'text-slate-500 hover:text-slate-800 hover:bg-white'}`}
                >
                  {activeCategory === cat && (
                    <motion.div
                      layoutId="project-tab-bg"
                      className="absolute inset-0 rounded-xl bg-gradient-to-r from-brand-blue to-blue-600"
                      transition={{ type: 'spring', stiffness: 400, damping: 36 }}
                    />
                  )}
                  <span className="relative z-10">{cat}</span>
                </button>
              ))}
            </div>
          </motion.div>

          {filteredProducts.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredProducts.map((product, i) => (
                <motion.div
                  key={product.name}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                  className="group bg-slate-50 rounded-[2rem] overflow-hidden border border-slate-200 hover:shadow-card hover:-translate-y-1 transition-all duration-300 flex flex-col h-full"
                >
                  <div className="h-48 overflow-hidden relative">
                    <div className={`absolute inset-0 bg-gradient-to-tr ${product.color} opacity-90`} />
                    <img src={product.img} alt={product.name} className="w-full h-full object-cover mix-blend-overlay opacity-60 group-hover:scale-105 transition-transform duration-500" />
                    <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between">
                      <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center shadow-lg text-slate-900" style={{ color: product.accent }}>
                        {product.icon}
                      </div>
                      <span className="px-3 py-1 bg-white/90 backdrop-blur text-slate-900 text-xs font-bold rounded-full font-space">
                        {product.badge}
                      </span>
                    </div>
                  </div>
                  
                  <div className="p-6 flex flex-col flex-grow">
                    <h3 className="font-syne font-bold text-xl text-slate-900 mb-2">{product.name}</h3>
                    <p className="font-inter text-slate-600 text-sm mb-6 flex-grow">{product.tagline}</p>
                    
                    {product.link ? (
                      <a href={product.link} className="inline-flex items-center gap-2 text-sm font-space font-semibold transition-colors duration-200" style={{ color: product.accent }}>
                        View Project <ArrowRight size={16} />
                      </a>
                    ) : (
                      <a href="/#contact" className="inline-flex items-center gap-2 text-sm font-space font-semibold transition-colors duration-200" style={{ color: product.accent }}>
                        Request Demo <ExternalLink size={16} />
                      </a>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center py-20 bg-slate-50 rounded-3xl border border-slate-200 shadow-sm"
            >
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-6 shadow-sm">
                <Zap size={24} className="text-slate-400" />
              </div>
              <h3 className="font-syne font-bold text-2xl text-slate-900 mb-3">Coming Soon</h3>
              <p className="font-inter text-slate-500 max-w-md mx-auto">
                We are currently engineering amazing software products for the {activeCategory} sector. Check back later!
              </p>
            </motion.div>
          )}
        </div>
      </div>

      {/* ── Client Projects Section ── */}
      <div className="py-24 bg-slate-50 relative z-10 overflow-hidden">
        {/* decorative blobs */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[400px] bg-brand-blue/4 rounded-full blur-[140px] pointer-events-none" />
        <div className="absolute bottom-0 right-0 w-[500px] h-[400px] bg-brand-orange/4 rounded-full blur-[120px] pointer-events-none" />

        <div className="max-w-7xl mx-auto px-6 relative z-10">

          {/* Section header */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="mb-16"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-brand-orange/30 bg-brand-orange/5 text-brand-orange text-sm font-semibold mb-5">
              <span className="w-2 h-2 rounded-full bg-brand-orange animate-pulse"></span>
              Delivered Solutions
            </div>
            <h2 className="font-syne font-extrabold text-3xl md:text-5xl text-slate-900 mb-4">
              Client Projects
            </h2>
            <div className="w-20 h-1.5 bg-gradient-to-r from-brand-orange to-brand-blue rounded-full mb-6"></div>
            <p className="text-slate-500 font-inter text-lg max-w-2xl leading-relaxed">
              From digital marketing to industrial operations — we've partnered with businesses across
              diverse domains to deliver software that creates lasting impact.
            </p>
          </motion.div>

          {/* Client cards grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7">
            {clientProjects.map((client, i) => (
              <motion.div
                key={client.name}
                initial={{ opacity: 0, y: 36 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.55, delay: i * 0.1 }}
                className={`group relative bg-gradient-to-br ${client.bg} border ${client.border} rounded-[2rem] p-7 hover:shadow-xl hover:-translate-y-1.5 transition-all duration-350 flex flex-col overflow-hidden`}
              >
                {/* subtle shine on hover */}
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                  style={{ background: `radial-gradient(ellipse at 20% 20%, ${client.accent}14 0%, transparent 60%)` }}
                />

                {/* Logo / Initial */}
                <div className="flex items-center gap-4 mb-5">
                  <div className="w-16 h-16 bg-white rounded-2xl shadow-md flex items-center justify-center overflow-hidden shrink-0 border border-white/60">
                    {client.logo ? (
                      <img
                        src={client.logo}
                        alt={`${client.name} logo`}
                        className="w-full h-full object-contain p-1"
                      />
                    ) : (
                      <div
                        className="w-full h-full flex items-center justify-center text-white text-2xl font-syne font-extrabold"
                        style={{ background: `linear-gradient(135deg, ${client.color}, ${client.color}cc)` }}
                      >
                        {client.initial}
                      </div>
                    )}
                  </div>
                  <div>
                    <h3 className="font-syne font-bold text-lg text-slate-900 leading-tight">
                      {client.name}
                    </h3>
                    <span
                      className="text-xs font-space font-semibold uppercase tracking-widest"
                      style={{ color: client.accent }}
                    >
                      {client.industry}
                    </span>
                  </div>
                </div>

                {/* Brief */}
                <p className="font-inter text-slate-600 text-sm leading-relaxed flex-grow mb-5">
                  {client.brief}
                </p>

                {/* Tags */}
                <div className="flex flex-wrap gap-2 mt-auto">
                  {client.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 rounded-full text-xs font-space font-semibold border"
                      style={{
                        color: client.accent,
                        borderColor: `${client.accent}40`,
                        background: `${client.accent}0f`,
                      }}
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                {/* accent bar bottom */}
                <div
                  className="absolute bottom-0 left-0 right-0 h-1 rounded-b-[2rem] opacity-60"
                  style={{ background: `linear-gradient(to right, ${client.accent}, transparent)` }}
                />
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
